import React from 'react';
import { Box, Grid, IconButton, Paper, Typography } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator as DragIndicatorIcon, Edit as EditIcon } from '@mui/icons-material';
import { FormLayout } from '../../types/form';

// ============================================================================
// Interfaces
// ============================================================================
interface SortableFieldItemProps {
  id: string;
  field: FormLayout['layout_config']['fields'][0];
  onEdit: () => void;
  isAvailable?: boolean;
}

// ============================================================================
// Component
// ============================================================================
export const SortableFieldItem = ({ id, field, onEdit, isAvailable }: SortableFieldItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ 
    id,
    transition: {
      duration: 200,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Grid item xs={12} sm={!isAvailable && field.width <= 50 ? 6 : 12}>
      <Box 
        ref={setNodeRef} 
        style={style}
        sx={{ 
          transform: isDragging ? 'scale(1.02)' : 'none',
          transition: 'transform 0.2s ease'
        }}
      >
        <Paper sx={{
          p: 2,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: isDragging ? 'action.hover' : 'background.paper',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderWidth: isDragging ? 2 : 1,
          borderStyle: 'solid',
          boxShadow: isDragging ? 4 : 1
        }}>
          <Box {...attributes} {...listeners} sx={{ mr: 1, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
            <DragIndicatorIcon color={isDragging ? 'primary' : 'action'} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: isDragging ? 'primary.main' : 'text.primary' }}>
              {field.label} ({field.name})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!isAvailable && `Width: ${field.width}% • `}{field.required ? 'Required' : 'Optional'}
            </Typography>
          </Box>
          {!isAvailable && (
            <IconButton size="small" onClick={onEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Paper>
      </Box>
    </Grid>
  );
};
